import lodash from 'lodash';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { IoMdClose } from 'react-icons/io';
import { UiSearch } from '@vyaguta/vyaguta-icons/ui';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import * as routes from 'constants/routes';

import {
  departmentsQueryOptions,
  designationsQueryOptions,
  engagementStatusesQueryOptions,
} from 'hooks/query/useLookupQueries';
import { usersQueryOptions } from 'hooks/query/useUserQueries';
import { useRolesQuery } from 'hooks/query/useRoles';

import { parse } from 'utils/queryParam';
import { handleError } from 'utils/errorHandler';
import { EMPLOYEES } from 'constants/permissions';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { MAKE_SEARCH_BY } from 'constants/fetchTypes';
import { extractFullName, pluralize } from 'utils/string';
import DropDownSearch from 'components/common/dropdownsearch';
import { handleUserOfflineMode } from 'utils/internetConnection';
import AvatarComponent from 'components/common/avatar/AvatarComponent';
import { useUserPermissions } from 'hooks/query/useUserInfo';
import {
  BREAKPOINTS,
  TYPE_USER_TEXT,
  MIN_DEBOUNCE_TIME,
  NO_USERS_FOUND_TEXT,
  MIN_USER_SEARCH_LENGTH,
} from 'constants/appConstants';
import { WORKING_SHIFTS_FILTER_OPTION } from 'constants/worklog';

import { QueryObject, QueryValue } from 'interface/queryParam';

interface FilterOption {
  value: string | number;
  label: string;
  loadingState?: boolean;
}

interface UserFilterOption extends FilterOption {
  empId: number | string;
}

type EachFilterOption = FilterOption | UserFilterOption;
type FiltersState = QueryObject;

interface EmployeesDataSummary {
  size: number;
  total: number;
}

interface PermissionsState {
  [key: string]: boolean;
}

interface EmployeesFilterOwnProps {
  data: EmployeesDataSummary;
  applyFilter: (filters: FiltersState) => void;
  setIsFilterApplied: (isApplied: boolean) => void;
  isFilterApplied: boolean;
  isDataLoading: boolean;
  location: { search: string };
}

type EmployeesFilterProps = EmployeesFilterOwnProps;

interface FilterSetting {
  query: string;
  loadingState: boolean;
  label: string;
  allowMultiple: boolean;
  getOptions: () => Promise<FilterOption[]> | FilterOption[];
}

interface NamedRow {
  id: number;
  name: string;
}

interface EngagementStatusRow {
  name: string;
}

interface UserRow {
  id: number;
  empId: string | number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
}

const isRecord = (value: object | null): value is Record<string, string | number | boolean | null | undefined> =>
  value !== null;

const readDataRows = (value: { data?: object[] } | object): object[] => {
  if ('data' in value && Array.isArray(value.data)) {
    return value.data;
  }

  return [];
};

const toNamedRow = (value: object): NamedRow => {
  if (!isRecord(value)) {
    return { id: 0, name: '' };
  }

  return {
    id: Number(value.id || 0),
    name: String(value.name || ''),
  };
};

const toUserRow = (value: object): UserRow => {
  if (!isRecord(value)) {
    return { id: 0, empId: '', firstName: '', middleName: '', lastName: '' };
  }

  return {
    id: Number(value.id || 0),
    empId: String(value.empId || ''),
    firstName: String(value.firstName || ''),
    middleName: value.middleName ? String(value.middleName) : '',
    lastName: String(value.lastName || ''),
  };
};

const getQueryStringValue = (value: QueryValue): string => (typeof value === 'string' ? value : '');

const toFilterOption = (option: EachFilterOption): FilterOption => ({ value: option.value, label: option.label });

const EmployeesFilter: React.FC<EmployeesFilterProps> = (props) => {
  const { data, applyFilter, setIsFilterApplied, isFilterApplied, isDataLoading } = props;
  const queryClient = useQueryClient();
  const { data: rolesFromQuery = [] } = useRolesQuery();
  const permissions = useUserPermissions() as PermissionsState;
  const queryParams = parse(props.location.search) as QueryObject;

  const [filters, setFilters] = useState<FiltersState>({ ...queryParams });
  const [isFetchingFilters, setIsFetchingFilters] = useState<boolean>(true);
  const [isFetchingLeaveIssuer, setIsFetchingLeaveIssuer] = useState<boolean>(false);
  const [isFetchingSupervisor, setIsFetchingSupervisor] = useState<boolean>(false);
  const [isFetchingAppraiser, setIsFetchingAppraiser] = useState<boolean>(false);
  const [isFilterChanged, setIsFilterChanged] = useState<boolean>(false);

  const [moreOptionsFilter, setMoreOptionsFilter] = useState<FilterOption[]>([]);
  const [rolesOptionsFilter, setRolesOptionsFilter] = useState<FilterOption[]>([]);
  const [departmentOptionsFilter, setDepartmentOptionsFilter] = useState<FilterOption[]>([]);
  const [engagmentStatusOptionsFilter, setEngagmentStatusOptionsFilter] = useState<FilterOption[]>([]);
  const [leaveIssuerOptionsFilter, setLeaveIssuerOptionsFilter] = useState<UserFilterOption[]>([]);
  const [appraiserOptionsFilter, setAppraiserOptionsFilter] = useState<UserFilterOption[]>([]);
  const [designationOptionsFilter, setDesignationOptionsFilter] = useState<FilterOption[]>([]);
  const [workingShiftOptionsFilter, setWorkingShiftOptionsFilter] = useState<FilterOption[]>(
    WORKING_SHIFTS_FILTER_OPTION as FilterOption[]
  );
  const [supervisorOptionsFilter, setSupervisorOptionsFilter] = useState<UserFilterOption[]>([]);

  const [rolesData, setRolesData] = useState<FilterOption[]>([]);
  const [departmentData, setDepartmentData] = useState<FilterOption[]>([]);
  const [engagementStatusData, setEngagmentStatusData] = useState<FilterOption[]>([]);
  const [leaveIssuerData, setLeaveIssuerData] = useState<UserFilterOption[]>([]);
  const [appraiserData, setAppraiserData] = useState<UserFilterOption[]>([]);
  const [designationData, setDesignationData] = useState<FilterOption[]>([]);
  const [supervisorData, setSupervisorData] = useState<UserFilterOption[]>([]);

  const [selectedMoreFilterList, setSelectedMoreList] = useState<FilterOption[]>([]);
  const [selectedDepartmentList, setSelectedDepartmentList] = useState<FilterOption[]>([]);
  const [selectedEngStatusList, setSelectedEngStatusList] = useState<FilterOption[]>([]);
  const [selectedLeaveIssuerList, setSelectedLeaveIssuerList] = useState<UserFilterOption[]>([]);
  const [selectedAppraiserList, setSelectedAppraiserList] = useState<UserFilterOption[]>([]);
  const [selectedSupervisorList, setSelectedSupervisorList] = useState<UserFilterOption[]>([]);
  const [selectedDesignationList, setSelectedDesignationList] = useState<FilterOption[]>([]);
  const [selectedWorkShiftList, setSelectedWorkShiftList] = useState<FilterOption[]>([]);
  const [selectedRolesList, setSelectedRolesList] = useState<FilterOption[]>([]);

  const [leaverIssuerCode, setLeaveIssuerCode] = useState<string>('');
  const [supervisorCode, setSupervisorCode] = useState<string>('');
  const [appraiserCode, setAppraiserCode] = useState<string>('');

  const debounceLoadingStates: Record<string, boolean> = {
    leaveIssuerId: isFetchingLeaveIssuer,
    coachId: isFetchingAppraiser,
    supervisorId: isFetchingSupervisor,
  };

  const isMobileView = useMediaQuery({ maxWidth: BREAKPOINTS.xs, minWidth: undefined });

  const filtersSettings: Record<string, FilterSetting> = useMemo(
    () => ({
      department: {
        query: 'departmentId',
        loadingState: isFetchingFilters,
        label: 'Department',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(departmentsQueryOptions())).map(toNamedRow);
          return rows.map((row) => ({ value: row.id, label: row.name }));
        },
      },
      employeeStatus: {
        query: 'empStatus',
        loadingState: isFetchingFilters,
        label: 'Employment Status',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(engagementStatusesQueryOptions()));
          return rows.map((row) => ({
            value: String((row as EngagementStatusRow).name || ''),
            label: String((row as EngagementStatusRow).name || ''),
          }));
        },
      },
      roles: {
        query: 'roleIds',
        loadingState: isFetchingFilters,
        label: 'Roles',
        allowMultiple: true,
        getOptions() {
          return rolesFromQuery.map((role) => ({ value: role.id ?? 0, label: role.name ?? '' }));
        },
      },
      leaveIssuer: {
        query: 'leaveIssuerId',
        loadingState: isFetchingLeaveIssuer,
        label: 'Leave Issuer',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(usersQueryOptions())).map(toUserRow);
          return rows.map((row) => ({ value: row.id, label: extractFullName(row) }));
        },
      },
      supervisor: {
        query: 'supervisorId',
        loadingState: isFetchingSupervisor,
        label: 'Supervisor',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(usersQueryOptions())).map(toUserRow);
          return rows.map((row) => ({ value: row.id, label: extractFullName(row) }));
        },
      },
      appraiser: {
        query: 'coachId',
        loadingState: isFetchingAppraiser,
        label: 'Appraiser',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(usersQueryOptions())).map(toUserRow);
          return rows.map((row) => ({ value: row.id, label: extractFullName(row) }));
        },
      },
      designation: {
        query: 'designationId',
        loadingState: isFetchingFilters,
        label: 'Designation',
        allowMultiple: true,
        async getOptions() {
          const rows = readDataRows(await queryClient.fetchQuery(designationsQueryOptions())).map(toNamedRow);
          return rows.map((row) => ({ value: row.id, label: row.name }));
        },
      },
      workingShift: {
        query: 'workingShiftId',
        loadingState: false,
        label: 'Working Shift Timing',
        allowMultiple: true,
        getOptions: () => WORKING_SHIFTS_FILTER_OPTION,
      },
    }),
    [isFetchingAppraiser, isFetchingFilters, isFetchingLeaveIssuer, isFetchingSupervisor, queryClient, rolesFromQuery]
  );

  useEffect(() => {
    (async () => {
      if (!leaverIssuerCode) {
        return;
      }

      if (leaverIssuerCode.trimStart().length < MIN_USER_SEARCH_LENGTH) {
        return;
      }

      try {
        setIsFetchingLeaveIssuer(true);

        const rows = readDataRows(
          await queryClient.fetchQuery(
            usersQueryOptions({
              q: leaverIssuerCode,
              searchBy: MAKE_SEARCH_BY.join(','),
            })
          )
        ).map(toUserRow);
        const leaveIssuerOptions = rows.map((row) => ({
          value: row.id,
          empId: row.empId,
          label: extractFullName(row),
        }));

        const filterOutList = !selectedLeaveIssuerList.length
          ? leaveIssuerOptions
          : leaveIssuerOptions.filter((leaveIssuer) => {
              return !selectedLeaveIssuerList.some((item) => item.value === leaveIssuer.value);
            });

        setLeaveIssuerData(filterOutList);
        setLeaveIssuerOptionsFilter(filterOutList);
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingLeaveIssuer(false);
      }
    })();
  }, [leaverIssuerCode, queryClient, selectedLeaveIssuerList]);

  useEffect(() => {
    (async () => {
      if (!supervisorCode) {
        return;
      }

      if (supervisorCode.trimStart().length < MIN_USER_SEARCH_LENGTH) {
        return;
      }

      try {
        setIsFetchingSupervisor(true);

        const rows = readDataRows(
          await queryClient.fetchQuery(
            usersQueryOptions({
              q: supervisorCode,
              searchBy: MAKE_SEARCH_BY.join(','),
            })
          )
        ).map(toUserRow);
        const supervisorOptions = rows.map((row) => ({
          value: row.id,
          empId: row.empId,
          label: extractFullName(row),
        }));

        setSupervisorData(supervisorOptions);
        setSupervisorOptionsFilter(supervisorOptions);
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingSupervisor(false);
      }
    })();
  }, [supervisorCode, queryClient]);

  useEffect(() => {
    (async () => {
      if (!appraiserCode) {
        return;
      }

      if (appraiserCode.trimStart().length < MIN_USER_SEARCH_LENGTH) {
        return;
      }

      try {
        setIsFetchingAppraiser(true);

        const rows = readDataRows(
          await queryClient.fetchQuery(
            usersQueryOptions({
              q: appraiserCode,
              searchBy: MAKE_SEARCH_BY.join(','),
            })
          )
        ).map(toUserRow);
        const appraiserOptions = rows.map((row) => ({
          value: row.id,
          empId: row.empId,
          label: extractFullName(row),
        }));

        setAppraiserData(appraiserOptions);
        setAppraiserOptionsFilter(appraiserOptions);
      } catch (error) {
        handleError(error);
      } finally {
        setIsFetchingAppraiser(false);
      }
    })();
  }, [appraiserCode, queryClient]);

  async function handleSelectedOptions(filterItem: string, query: string) {
    const shouldApplySelectedQueries = ['leaverIssuer', 'supervisor', 'appraiser'].includes(filterItem);

    if (shouldApplySelectedQueries) {
      return;
    }

    const selectedQueryValue = getQueryStringValue(filters[query]);
    const selectedQueries = !selectedQueryValue ? [] : selectedQueryValue.split(',');

    if (!selectedQueries.length) {
      return;
    }
    const filterList = (await handleFetchFilter(query)) || [];

    const selectedOptions = selectedQueries.map((selectedId) => {
      const selectedItem = filterList.reduce<FilterOption>(
        (acc, current) => {
          if (`${current.value}` === selectedId) {
            return {
              value: selectedId,
              label: current.label,
            };
          }

          return acc;
        },
        { value: '', label: '' }
      );

      return selectedItem;
    });

    handleAlreadySelected(query, selectedQueries, selectedOptions);
    const setFilterObj = { value: filtersSettings[filterItem].query, label: filtersSettings[filterItem].label };
    setMoreOptionsFilter((moreData) => moreData.filter((item) => item.value !== query));
    setSelectedMoreList((prevData) => [...prevData, setFilterObj]);
  }

  const handleAlreadySelected = (query: string, selectedQueries: string[], selectedOptions: FilterOption[]) => {
    switch (query) {
      case filtersSettings.roles.query:
        setRolesOptionsFilter((roleData) => roleData.filter((item) => !selectedQueries.includes(`${item.value}`)));
        setSelectedRolesList(selectedOptions);
        break;
      case filtersSettings.department.query:
        setDepartmentOptionsFilter((depData) => depData.filter((item) => !selectedQueries.includes(`${item.value}`)));
        setSelectedDepartmentList(selectedOptions);
        break;
      case filtersSettings.employeeStatus.query:
        setEngagmentStatusOptionsFilter((engData) =>
          engData.filter((item) => !selectedQueries.includes(`${item.value}`))
        );
        setSelectedEngStatusList(selectedOptions);
        break;
      case filtersSettings.designation.query:
        setDesignationOptionsFilter((desgData) =>
          desgData.filter((item) => !selectedQueries.includes(`${item.value}`))
        );
        setSelectedDesignationList(selectedOptions);
        break;
      case filtersSettings.workingShift.query:
        setWorkingShiftOptionsFilter((workShiftData) =>
          workShiftData.filter((item) => !selectedQueries.includes(`${item.value}`))
        );
        setSelectedWorkShiftList(selectedOptions);
        break;
      default:
        break;
    }
  };

  const updateFilter = useCallback(
    (newState: FiltersState) => {
      const updatedNewState = Object.entries(newState).reduce<FiltersState>((acc, [key, value]) => {
        if (!value) {
          return acc;
        }

        if (Array.isArray(value) && !value.length) {
          return acc;
        }

        if (Array.isArray(value)) {
          return { [key]: key === 'q' ? value.join('') : value.join(','), ...acc };
        }

        return { [key]: value, ...acc };
      }, {});

      setFilters((prevState) => {
        const isFilterChange = !lodash.isEqual(queryParams, updatedNewState);
        setIsFilterChanged(isFilterChange);

        return { ...prevState, ...newState };
      });
    },
    [queryParams]
  );

  const handleApplyFilter = () => {
    applyFilter(filters);
    setIsFilterChanged(false);
    setIsFilterApplied(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.trimLeft();
    const filterSearchValue = getQueryStringValue(queryParams.q);

    updateFilter({ q: searchValue });

    if (searchValue === filterSearchValue) {
      setIsFilterChanged(false);
    }
  };

  const handleSearchClose = () => {
    const updatedFilters = { ...filters, q: undefined };

    if (!queryParams.q) {
      setFilters(updatedFilters);
      setIsFilterChanged(false);

      return;
    }

    setFilters(updatedFilters);
    applyFilter(updatedFilters);
    setIsFilterChanged(false);
  };

  useEffect(() => {
    const isRolesFilterAbsent =
      !selectedRolesList.length &&
      !selectedSupervisorList.length &&
      !selectedAppraiserList.length &&
      !selectedDepartmentList.length &&
      !selectedDesignationList.length &&
      !selectedEngStatusList.length &&
      !selectedLeaveIssuerList.length &&
      !selectedWorkShiftList.length;

    if (isRolesFilterAbsent) {
      return;
    }
  }, [
    selectedRolesList,
    selectedDepartmentList,
    selectedDesignationList,
    selectedWorkShiftList,
    selectedEngStatusList,
    selectedLeaveIssuerList,
    selectedAppraiserList,
    selectedSupervisorList,
  ]);

  const handleFetchFilter = async (item: string): Promise<FilterOption[]> => {
    try {
      switch (item) {
        case filtersSettings.department.query: {
          const department = await filtersSettings.department.getOptions();
          setDepartmentData(department);
          setDepartmentOptionsFilter(department);

          return department;
        }
        case filtersSettings.employeeStatus.query: {
          const engagementStatus = await filtersSettings.employeeStatus.getOptions();
          setEngagmentStatusData(engagementStatus);
          setEngagmentStatusOptionsFilter(engagementStatus);

          return engagementStatus;
        }
        case filtersSettings.roles.query: {
          const roles = await filtersSettings.roles.getOptions();
          const sortedRoles = lodash.sortBy(roles, ['label']);
          setRolesData(sortedRoles);
          setRolesOptionsFilter(sortedRoles);

          return sortedRoles;
        }
        case filtersSettings.designation.query: {
          const designation = await filtersSettings.designation.getOptions();
          setDesignationData(designation);
          setDesignationOptionsFilter(designation);

          return designation;
        }
        case filtersSettings.workingShift.query: {
          const workingShift = await filtersSettings.workingShift.getOptions();
          setWorkingShiftOptionsFilter(workingShift);

          return workingShift;
        }
        default:
          return [];
      }
    } catch (error) {
      handleError(error);
      return [];
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      if (!permissions[EMPLOYEES.FILTER]) {
        return;
      }
      const moreList = Object.keys(filtersSettings).map((item) => ({
        value: filtersSettings[item].query,
        label: filtersSettings[item].label,
      }));
      setMoreOptionsFilter(moreList);

      Object.keys(filtersSettings).forEach((filter) => {
        handleSelectedOptions(filter, filtersSettings[filter].query);
      });
      setIsFetchingFilters(false);
    })();
  }, [permissions]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleOnMoreFilterChange = (options: FilterOption[] | null) => {
    const selectedOptions = options ?? [];
    setSelectedMoreList(selectedOptions);
    if (selectedOptions.length === 0) {
      const moreList = Object.keys(filtersSettings).map((item) => ({
        value: filtersSettings[item].query,
        loadingState: filtersSettings[item].loadingState,
        label: filtersSettings[item].label,
      }));
      setMoreOptionsFilter(moreList);
      setSelectedMoreList([]);

      return;
    }
    handleFetchFilter(selectedOptions[selectedOptions.length - 1].value.toString());
    const filteredList = moreOptionsFilter.filter(
      (moreFilter) => moreFilter.value !== selectedOptions[selectedOptions.length - 1].value
    );
    setMoreOptionsFilter(filteredList);
  };

  const handleEachFilterChange = (item: string, options: EachFilterOption[] | null) => {
    const selectedOptions = options ?? [];

    switch (item) {
      case filtersSettings.department.query: {
        const nextOptions = selectedOptions.map(toFilterOption);
        setSelectedDepartmentList(nextOptions);
        if (selectedOptions.length === 0) {
          setDepartmentOptionsFilter(departmentData);
          setSelectedDepartmentList([]);
          updateFilter({ departmentId: undefined });

          return;
        }
        updateFilter({ departmentId: nextOptions.map(({ value }) => value) });
        const departmentFilterList = departmentOptionsFilter.filter(
          (department) => department.value !== toFilterOption(selectedOptions[selectedOptions.length - 1]).value
        );

        setDepartmentOptionsFilter(departmentFilterList);
        break;
      }
      case filtersSettings.employeeStatus.query: {
        const nextOptions = selectedOptions.map(toFilterOption);
        setSelectedEngStatusList(nextOptions);
        if (selectedOptions.length === 0) {
          setEngagmentStatusOptionsFilter(engagementStatusData);
          setSelectedEngStatusList([]);
          updateFilter({ empStatus: undefined });

          return;
        }
        updateFilter({ empStatus: nextOptions.map(({ value }) => value) });
        const empFilteredList = engagmentStatusOptionsFilter.filter(
          (empStatus) => empStatus.value !== toFilterOption(selectedOptions[selectedOptions.length - 1]).value
        );

        setEngagmentStatusOptionsFilter(empFilteredList);
        break;
      }
      case filtersSettings.roles.query: {
        const nextOptions = selectedOptions.map(toFilterOption);
        setSelectedRolesList(nextOptions);
        if (selectedOptions.length === 0) {
          setRolesOptionsFilter(rolesData);
          setSelectedRolesList([]);
          updateFilter({ roleIds: undefined });

          return;
        }
        updateFilter({ roleIds: nextOptions.map(({ value }) => value) });
        const roleFilteredList = rolesOptionsFilter.filter(
          (role) => role.value !== toFilterOption(selectedOptions[selectedOptions.length - 1]).value
        );

        setRolesOptionsFilter(roleFilteredList);
        break;
      }
      case filtersSettings.leaveIssuer.query: {
        const nextOptions = selectedOptions as UserFilterOption[];
        setSelectedLeaveIssuerList(nextOptions);
        setLeaveIssuerCode('');
        if (selectedOptions.length === 0) {
          setLeaveIssuerOptionsFilter(leaveIssuerData);
          setSelectedLeaveIssuerList([]);
          updateFilter({ leaveIssuerId: undefined });

          return;
        }
        updateFilter({ leaveIssuerId: nextOptions.map(({ value }) => value) });
        const leaveIssuerList = leaveIssuerOptionsFilter.filter(
          (leaveIssuer) => leaveIssuer.value !== (selectedOptions[selectedOptions.length - 1] as UserFilterOption).value
        );

        setLeaveIssuerOptionsFilter(leaveIssuerList);
        break;
      }
      case filtersSettings.supervisor.query: {
        const nextOptions = selectedOptions as UserFilterOption[];
        setSelectedSupervisorList(nextOptions);
        if (selectedOptions.length === 0) {
          setSupervisorOptionsFilter(supervisorData);
          setSelectedSupervisorList([]);
          updateFilter({ supervisorId: undefined });

          return;
        }
        updateFilter({ supervisorId: nextOptions.map(({ value }) => value) });
        const supervisorFilterdList = supervisorOptionsFilter.filter(
          (supervisor) => supervisor.value !== (selectedOptions[selectedOptions.length - 1] as UserFilterOption).value
        );

        setSupervisorOptionsFilter(supervisorFilterdList);
        break;
      }
      case filtersSettings.appraiser.query: {
        const nextOptions = selectedOptions as UserFilterOption[];
        setSelectedAppraiserList(nextOptions);
        if (selectedOptions.length === 0) {
          setAppraiserOptionsFilter(appraiserData);
          setSelectedAppraiserList([]);
          updateFilter({ coachId: undefined });

          return;
        }
        updateFilter({ coachId: nextOptions.map(({ value }) => value) });
        const appraiserFilterList = appraiserOptionsFilter.filter(
          (appraiser) => appraiser.value !== (selectedOptions[selectedOptions.length - 1] as UserFilterOption).value
        );

        setAppraiserOptionsFilter(appraiserFilterList);
        break;
      }
      case filtersSettings.designation.query: {
        const nextOptions = selectedOptions.map(toFilterOption);
        setSelectedDesignationList(nextOptions);
        if (selectedOptions.length === 0) {
          setDesignationOptionsFilter(designationData);
          setSelectedDesignationList([]);
          updateFilter({ designationId: undefined });

          return;
        }
        updateFilter({ designationId: nextOptions.map(({ value }) => value) });
        const designationFilteredList = designationOptionsFilter.filter(
          (designation) => designation.value !== toFilterOption(selectedOptions[selectedOptions.length - 1]).value
        );

        setDesignationOptionsFilter(designationFilteredList);
        break;
      }
      case filtersSettings.workingShift.query:
        if (selectedOptions.length === 0) {
          setWorkingShiftOptionsFilter(WORKING_SHIFTS_FILTER_OPTION);
          setSelectedWorkShiftList([]);
          updateFilter({ workingShiftId: undefined });
        } else {
          const workingShiftFilteredList = workingShiftOptionsFilter.filter(
            (workingShift) => workingShift.value !== toFilterOption(selectedOptions[selectedOptions.length - 1]).value
          );

          setWorkingShiftOptionsFilter(workingShiftFilteredList);
          const nextOptions = selectedOptions.map(toFilterOption);
          setSelectedWorkShiftList(nextOptions);
          updateFilter({ workingShiftId: nextOptions.map(({ value }) => value) });
        }
        break;
      default:
        break;
    }
  };

  const handleRemoveFilter = (item: FilterOption, index: number, optionsArray: FilterOption[]) => {
    handleEachFilterChange(item.value.toString(), []);
    optionsArray.splice(index, 1);
    const options = [...optionsArray];
    handleOnMoreFilterChange(options);
  };

  const handleRemoveFilters = () => {
    setFilters({});
    setIsFilterApplied(false);
    setSelectedRolesList([]);
    setSelectedAppraiserList([]);
    setSelectedDepartmentList([]);
    setSelectedDesignationList([]);
    setSelectedEngStatusList([]);
    setSelectedLeaveIssuerList([]);
    setSelectedSupervisorList([]);
    setDepartmentOptionsFilter(departmentData);
    setDesignationData(designationData);
    setEngagmentStatusOptionsFilter(engagementStatusData);
    setRolesOptionsFilter(rolesData);
    setSelectedWorkShiftList([]);
    applyFilter({});
  };

  const handleInputChange = (item: string, code: string) => {
    if (!code) {
      return;
    }
    switch (item) {
      case filtersSettings.leaveIssuer.query:
        if (code.trimStart().length < MIN_USER_SEARCH_LENGTH) {
          setIsFetchingLeaveIssuer(false);

          return;
        }

        setIsFetchingLeaveIssuer(false);
        leaveIssuerDebounceFunction.current(code);
        break;

      case filtersSettings.supervisor.query:
        if (code.trimStart().length < MIN_USER_SEARCH_LENGTH) {
          setIsFetchingSupervisor(false);

          return;
        }

        setIsFetchingSupervisor(false);
        supervisorDebounceFunction.current(code);
        break;

      case filtersSettings.appraiser.query:
        if (code.trimStart().length < MIN_USER_SEARCH_LENGTH) {
          setIsFetchingAppraiser(false);

          return;
        }

        setIsFetchingAppraiser(false);
        appraiserDebounceFunction.current(code);
        break;

      default:
        break;
    }
  };

  const leaveIssuerDebounceFunction = useRef(
    lodash.debounce((code: string) => {
      setLeaveIssuerCode(code);
    }, MIN_DEBOUNCE_TIME)
  );

  const supervisorDebounceFunction = useRef(
    lodash.debounce((code: string) => {
      setSupervisorCode(code);
    }, MIN_DEBOUNCE_TIME)
  );

  const appraiserDebounceFunction = useRef(
    lodash.debounce((code: string) => {
      setAppraiserCode(code);
    }, MIN_DEBOUNCE_TIME)
  );

  useEffect(() => {
    const leaveIssuerDebouncedFn = leaveIssuerDebounceFunction.current;
    const supervisorDebouncedFn = supervisorDebounceFunction.current;
    const appraiserDebouncedFn = appraiserDebounceFunction.current;

    return () => {
      leaveIssuerDebouncedFn.cancel();
      supervisorDebouncedFn.cancel();
      appraiserDebouncedFn.cancel();
    };
  }, []);

  const handleNoOptionsMessage = (item: string) => {
    switch (item) {
      case filtersSettings.leaveIssuer.query:
        return leaverIssuerCode ? NO_USERS_FOUND_TEXT : TYPE_USER_TEXT;
      case filtersSettings.supervisor.query:
        return supervisorCode ? NO_USERS_FOUND_TEXT : TYPE_USER_TEXT;
      case filtersSettings.appraiser.query:
        return appraiserCode ? NO_USERS_FOUND_TEXT : TYPE_USER_TEXT;
      default:
        return 'No options';
    }
  };

  const getEachFilterData = (item: string): EachFilterOption[] => {
    switch (item) {
      case filtersSettings.department.query:
        return selectedDepartmentList;
      case filtersSettings.employeeStatus.query:
        return selectedEngStatusList;
      case filtersSettings.roles.query:
        return selectedRolesList;
      case filtersSettings.leaveIssuer.query:
        return selectedLeaveIssuerList;
      case filtersSettings.supervisor.query:
        return selectedSupervisorList;
      case filtersSettings.appraiser.query:
        return selectedAppraiserList;
      case filtersSettings.designation.query:
        return selectedDesignationList;
      case filtersSettings.workingShift.query:
        return selectedWorkShiftList;
      default:
        return [];
    }
  };

  const getEachFilterOptions = (item: string): EachFilterOption[] => {
    switch (item) {
      case filtersSettings.department.query:
        return !departmentOptionsFilter ? [] : lodash.sortBy(departmentOptionsFilter, ['label']);
      case filtersSettings.employeeStatus.query:
        return engagmentStatusOptionsFilter;
      case filtersSettings.roles.query:
        return rolesOptionsFilter;
      case filtersSettings.leaveIssuer.query:
        return leaveIssuerOptionsFilter;
      case filtersSettings.supervisor.query:
        return supervisorOptionsFilter;
      case filtersSettings.appraiser.query:
        return appraiserOptionsFilter;
      case filtersSettings.designation.query:
        return !designationOptionsFilter ? [] : lodash.sortBy(designationOptionsFilter, ['label']);
      case filtersSettings.workingShift.query:
        return WORKING_SHIFTS_FILTER_OPTION;
      default:
        return [];
    }
  };

  function isUserListFilter(item: string): boolean {
    return [
      filtersSettings.leaveIssuer.query,
      filtersSettings.supervisor.query,
      filtersSettings.appraiser.query,
    ].includes(item);
  }

  function handleOnKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
    let shouldApplyFilter = false;

    if (isMobileView && (e.currentTarget.value.length >= MIN_USER_SEARCH_LENGTH || e.currentTarget.value === '')) {
      shouldApplyFilter = true;
    }

    if (e.key === 'Enter' && isFilterChanged) {
      shouldApplyFilter = true;
    }

    return shouldApplyFilter && handleApplyFilterBtnClick();
  }

  const handleApplyFilterBtnClick = () =>
    handleUserOfflineMode(() => {
      handleApplyFilter();
      return true;
    });

  const handleRemoveFiltersBtnClick = () =>
    handleUserOfflineMode(() => {
      handleRemoveFilters();
      return true;
    });

  return (
    <div className="full-scope-card__header">
      <div className="employees__header name-wrap">
        <div className="employees__main-title">
          <div>
            <div className="employees__title">Leapfroggers</div>
            {!isDataLoading && (
              <div className="employees__subtitle">
                Showing {data.size} of {data.total} {pluralize('Leapfrogger', data.total)}
              </div>
            )}
          </div>
          <div
            className="employees__search-bar d-flex flex-row align-items-center"
            title="Search by ID, Name, Country, Location, Designation, Blood Group etc."
          >
            <UiSearch className="employees__search-icon" />
            <input
              className="employees__search-input"
              value={getQueryStringValue(filters.q)}
              type="text"
              onKeyUp={handleOnKeyUp}
              onChange={handleSearchChange}
              placeholder="Search leapfroggers..."
              data-cy="search-input"
            />
            {getQueryStringValue(filters.q).trim().length > 0 && (
              <IoMdClose className="employees__search-icon__close" onClick={handleSearchClose} />
            )}
          </div>
        </div>
        <div className="d-flex flex-row align-items-center ">
          <div className="employees__filter-bar title-bar__filter-buttons d-flex align-items-center f-wrap">
            {permissions[EMPLOYEES.FILTER] && (
              <div className="sm-d-none">
                <DropDownSearch
                  showSelected={true}
                  title="More Filters"
                  values={selectedMoreFilterList}
                  isLoading={isFetchingFilters}
                  options={moreOptionsFilter}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  className={classNames('lf-select')}
                  onChange={handleOnMoreFilterChange}
                  searchLabel="Select"
                  data-cy="more-filter"
                />
              </div>
            )}

            <button
              className={classNames('button ml-10 sm-d-none', {
                'button--primary': !isFilterChanged,
                'button--secondary': isFilterChanged,
              })}
              onClick={isFilterChanged ? handleApplyFilterBtnClick : undefined}
              data-cy="apply-filter"
            >
              Apply Filter
            </button>

            <button
              className={classNames('button__reset ml-10  sm-d-none', {
                'button__reset--active': isFilterApplied,
              })}
              onClick={isFilterApplied ? handleRemoveFiltersBtnClick : undefined}
              data-cy="reset-filter"
            >
              Reset
            </button>

            {permissions[EMPLOYEES.CREATE] && (
              <Link className="add-employee-button ml-20" to={routes.CREATE_EMPLOYEE} data-cy="add-leapfrogger">
                Add <span className="md-d-none sm-d-none">Leapfrogger</span>
              </Link>
            )}
          </div>
        </div>
      </div>
      {!isFetchingFilters && (
        <div className={classNames('employees employees__more-filters sm-d-none')}>
          {selectedMoreFilterList.map((item, index, array) => {
            return (
              <DropDownSearch
                key={`dropDownSearch${index}`}
                showSelected={true}
                title={item.label}
                values={getEachFilterData(item.value.toString())}
                isRemovable
                isLoading={Boolean(item.loadingState)}
                isDebounceLoading={debounceLoadingStates[item.value.toString()]}
                handleInputChange={(code: string) => handleInputChange(item.value.toString(), code)}
                noOptionsMessage={
                  isUserListFilter(item.value.toString())
                    ? () => handleNoOptionsMessage(item.value.toString())
                    : () => 'No options'
                }
                options={getEachFilterOptions(item.value.toString())}
                components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                className={classNames('lf-select')}
                onChange={(options: FilterOption[]) => handleEachFilterChange(item.value.toString(), options ?? [])}
                onRemove={() => handleRemoveFilter(item, index, array)}
                searchLabel="All"
                iconComponent={
                  isUserListFilter(item.value.toString())
                    ? (option) => (
                        <AvatarComponent
                          value={String(option.value)}
                          label={option.label}
                          empId={(option as UserFilterOption).empId?.toString()}
                        />
                      )
                    : undefined
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmployeesFilter;
